import { hasInputLikeFocus } from '@src/utils/dom/inputElement';
import { useCallback, useEffect } from 'react';
import type { PageInfo } from './useControlAreaNavigation';
import type { ScrollHandlers } from './useControlAreaScroll';

/**
 * スクロールコンテナを取得
 */
function getScrollContainer(): HTMLElement | null {
  return document.querySelector('[data-scroll-container="true"]') as HTMLElement;
}

/**
 * ControlAreaのキーボードショートカットを提供するカスタムフック
 *
 * @param pageInfo - ページ情報
 * @param scrollHandlers - スクロールハンドラー
 */
export function useControlAreaKeyboard(
  pageInfo: PageInfo | null,
  scrollHandlers: ScrollHandlers
): void {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      // 入力フィールドにフォーカスがある場合は動作しない
      if (hasInputLikeFocus()) {
        return;
      }

      const scrollContainer = getScrollContainer();

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          scrollContainer?.scrollBy({ top: -80, behavior: 'smooth' });
          break;
        case 'ArrowDown':
          event.preventDefault();
          scrollContainer?.scrollBy({ top: 80, behavior: 'smooth' });
          break;
        case 'PageUp':
        case 'ArrowLeft':
          event.preventDefault();
          if (!pageInfo?.isFirst) {
            scrollHandlers.handlePrevPage();
          }
          break;
        case 'PageDown':
        case 'ArrowRight':
          event.preventDefault();
          if (!pageInfo?.isLast) {
            scrollHandlers.handleNextPage();
          }
          break;
        case 'Home':
          event.preventDefault();
          scrollHandlers.handleScrollToTop();
          break;
        case 'End':
          event.preventDefault();
          scrollContainer?.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: 'smooth',
          });
          break;
      }
    },
    [pageInfo?.isFirst, pageInfo?.isLast, scrollHandlers]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}
